import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

// Request tracking to prevent duplicates
const activeRequests = new Map();

// Async thunks
export const fetchCounterOrders = createAsyncThunk(
  'counterOrders/fetchCounterOrders',
  async ({ restaurantId, page = 1, limit = 20, forceRefresh = false }, { rejectWithValue, getState, signal }) => {
    
    if (!restaurantId) {
      console.log('fetchCounterOrders: No restaurant ID provided');
      return rejectWithValue('Restaurant ID is required');
    }

    // Create a unique key for this request
    const requestKey = `fetch-${restaurantId}-${page}-${limit}`;
    
    // Check if this exact request is already in progress
    if (activeRequests.has(requestKey)) {
      console.log('fetchCounterOrders: Duplicate request prevented for', requestKey);
      return rejectWithValue('Duplicate request prevented');
    }

    const state = getState();
    const { counterOrders } = state;

    // Check cache validity (30 seconds)
    if (!forceRefresh &&
        counterOrders.lastFetched &&
        counterOrders.cachedRestaurantId === restaurantId &&
        (Date.now() - counterOrders.lastFetched) < 30000 &&
        counterOrders.orders.length > 0) {
      console.log('fetchCounterOrders: Using cached data');
      return {
        orders: counterOrders.orders,
        totalOrders: counterOrders.totalOrders,
        currentPage: counterOrders.currentPage,
        totalPages: counterOrders.totalPages
      };
    }

    // Mark request as active
    activeRequests.set(requestKey, true);
    console.log('fetchCounterOrders: Making API call for', requestKey);

    try {
      // Create timeout controller
      const timeoutId = setTimeout(() => {
        if (signal && !signal.aborted) {
          console.log('fetchCounterOrders: Request timeout after 15 seconds');
        }
      }, 15000);

      const response = await axios.get(`/api/orders/counter/${restaurantId}`, {
        params: { page, limit },
        timeout: 150, // 150ms timeout
        signal // Pass abort signal
      });
      
      clearTimeout(timeoutId);
      console.log('fetchCounterOrders: API response received', response.data);
      return response.data;
    } catch (error) {
      console.error('fetchCounterOrders: API error', error);
      
      // Handle different types of errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Request timeout - the server is taking too long to respond. Please try again.');
      }
      
      if (error.response?.status === 408) {
        throw new Error('Server timeout - please try again in a moment.');
      }
      
      throw error.response?.data?.message || error.message || 'Failed to fetch counter orders';
    } finally {
      // Always remove from active requests
      activeRequests.delete(requestKey);
      console.log('fetchCounterOrders: Request completed, removed from active requests');
    }
  }
);

export const createCounterOrder = createAsyncThunk(
  'counterOrders/createCounterOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.COUNTER_ORDERS_ADD, orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create counter order');
    }
  }
);

export const updateCounterOrder = createAsyncThunk(
  'counterOrders/updateCounterOrder',
  async ({ orderId, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.ORDER_BY_ID(orderId), updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update counter order');
    }
  }
);

export const deleteCounterOrder = createAsyncThunk(
  'counterOrders/deleteCounterOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      await axios.delete(API_ENDPOINTS.ORDER_BY_ID(orderId));
      return orderId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete counter order');
    }
  }
);

const initialState = {
  orders: [],
  loading: false,
  error: null,
  totalOrders: 0,
  currentPage: 1,
  totalPages: 1,
  lastFetched: null,
  cachedRestaurantId: null,
  initialized: false,
};

const counterOrderSlice = createSlice({
  name: 'counterOrders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetCounterOrders: (state) => {
      return {
        ...initialState,
        initialized: false
      };
    },
    setInitialized: (state, action) => {
      state.initialized = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Counter Orders
      .addCase(fetchCounterOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCounterOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        if (!action.payload.fromCache) {
          state.orders = action.payload.orders || [];
          state.totalOrders = action.payload.totalOrders || 0;
          state.currentPage = action.payload.currentPage || 1;
          state.totalPages = action.payload.totalPages || 1;
          state.lastFetched = Date.now();
          state.cachedRestaurantId = action.payload.restaurantId;
        }
        state.initialized = true;
      })
      .addCase(fetchCounterOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.initialized = true;
      })
      
      // Create Counter Order
      .addCase(createCounterOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCounterOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.unshift(action.payload);
        state.totalOrders += 1;
      })
      .addCase(createCounterOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Counter Order
      .addCase(updateCounterOrder.fulfilled, (state, action) => {
        const index = state.orders.findIndex(order => order._id === action.payload._id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      
      // Delete Counter Order
      .addCase(deleteCounterOrder.fulfilled, (state, action) => {
        state.orders = state.orders.filter(order => order._id !== action.payload);
        state.totalOrders = Math.max(0, state.totalOrders - 1);
      });
  },
});

// Selectors
export const selectCounterOrders = (state) => state.counterOrders.orders;
export const selectCounterOrdersLoading = (state) => state.counterOrders.loading;
export const selectCounterOrdersError = (state) => state.counterOrders.error;
export const selectCounterOrdersCurrentPage = (state) => state.counterOrders.currentPage;
export const selectCounterOrdersTotalPages = (state) => state.counterOrders.totalPages;
export const selectCounterOrdersTotalOrders = (state) => state.counterOrders.totalOrders;

export const { clearError, resetCounterOrders, setInitialized } = counterOrderSlice.actions;
export default counterOrderSlice.reducer;